import { useEffect, useState } from "react";

import { GET_DESIGNATION_LIST } from "../../../../config/Endpoints.js";
import type { SelectOption } from "../../ui/select/select.tsx";
import { getApi } from "../../../utils/api.ts";
import { accessToken } from "../../../utils/auth.ts";

function unwrapRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  const data = (payload as { data?: unknown }).data;
  return Array.isArray(data) ? data : [];
}

function toOptions(payload: unknown): SelectOption[] {
  return unwrapRows(payload).flatMap((row) => {
    if (!row || typeof row !== "object") return [];
    const item = row as Record<string, unknown>;
    const id = item.id ?? item.designationId;
    const name = item.name ?? item.designation;
    return id != null && typeof name === "string" && name.trim()
      ? [{ value: String(id), label: name.trim() }]
      : [];
  });
}

export function useDesignationOptions(departmentId: number | string | null | undefined) {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const id = String(departmentId ?? "").trim();
    setOptions([]);
    setError(null);
    if (!id) return () => controller.abort();

    setLoading(true);
    void getApi<unknown>(
      `${GET_DESIGNATION_LIST}?departmentId=${encodeURIComponent(id)}`,
      accessToken(),
      "json",
      controller.signal,
    ).then(({ data }) => setOptions(toOptions(data)))
      .catch(() => {
        if (!controller.signal.aborted) setError("Failed to load designations.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [departmentId]);

  return { options, loading, error };
}
